import * as Sentry from '@sentry/vue';

const CONFIG_ELEMENT_ID = 'sentry-browser-config';
const VUE_INTEGRATION_NAME = 'Vue';
const BROWSER_TRACING_INTEGRATION_NAME = 'BrowserTracing';
const REPLAY_INTEGRATION_NAME = 'Replay';
const EXTERNAL_OBJECT_NOT_FOUND_REJECTION_PATTERN = /Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+$/;

let initialized = false;

const parseSampleRate = (value, fallback = 0.1) => {
  if (value === null || value === undefined || value === '') return fallback;

  const parsed = Number(value);
  if (Number.isNaN(parsed)) return fallback;

  return Math.max(0, Math.min(1, parsed));
};

const eventExceptions = (event) => event?.exception?.values || [];

const isExternalObjectNotFoundRejection = (event) => eventExceptions(event).some((exception) => {
  if (exception?.type !== 'UnhandledRejection') return false;

  const value = String(exception?.value || '');
  return EXTERNAL_OBJECT_NOT_FOUND_REJECTION_PATTERN.test(value);
});

const beforeSend = (event) => {
  if (isExternalObjectNotFoundRejection(event)) return null;

  return event;
};

export const readSentryBrowserConfig = (documentRef = document) => {
  const element = documentRef.getElementById(CONFIG_ELEMENT_ID);
  if (!element?.textContent) return { enabled: false };

  try {
    return JSON.parse(element.textContent);
  } catch {
    return { enabled: false };
  }
};

export const sentryConfigFromRuntime = (runtimeConfig = readSentryBrowserConfig()) => {
  if (!runtimeConfig.enabled || !runtimeConfig.dsn) return { enabled: false };

  return {
    enabled: true,
    dsn: runtimeConfig.dsn,
    environment: runtimeConfig.environment || 'production',
    release: runtimeConfig.release || undefined,
    tracesSampleRate: parseSampleRate(runtimeConfig.tracesSampleRate, 0.1),
    replaysSessionSampleRate: parseSampleRate(runtimeConfig.replaysSessionSampleRate, 0.1),
    replaysOnErrorSampleRate: parseSampleRate(runtimeConfig.replaysOnErrorSampleRate, 1.0)
  };
};

export const initializeSentry = ({ app, runtimeConfig, surface = 'frontend' } = {}) => {
  const config = sentryConfigFromRuntime(runtimeConfig);
  if (!config.enabled) return false;
  if (initialized) return true;

  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: { request: false, response: false },
      httpBodies: [],
      urlQueryParams: false,
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
      graphQL: { document: false, variables: false },
      stackFrameVariables: false
    },
    tracesSampleRate: config.tracesSampleRate,
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    beforeSend,
    integrations: (defaultIntegrations = []) => {
      const integrations = defaultIntegrations.filter((integration) => (
        integration?.name !== VUE_INTEGRATION_NAME &&
        integration?.name !== BROWSER_TRACING_INTEGRATION_NAME &&
        integration?.name !== REPLAY_INTEGRATION_NAME
      ));

      if (app && typeof Sentry.vueIntegration === 'function') {
        integrations.push(Sentry.vueIntegration({ app, attachProps: false }));
      }

      integrations.push(Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          /^\//,
          /^https:\/\/flopinio\.com/
        ]
      }));

      if (typeof Sentry.replayIntegration === 'function') {
        integrations.push(Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
          networkCaptureBodies: false
        }));
      }

      return integrations;
    },
    initialScope: {
      tags: {
        surface
      }
    }
  });

  initialized = true;
  return true;
};

export const captureInertiaNavigation = (page) => {
  if (!initialized || !page) return false;

  Sentry.setTag('inertia.component', page.component || 'unknown');
  Sentry.addBreadcrumb({
    category: 'inertia.navigation',
    message: page.component || 'unknown',
    level: 'info',
    data: {
      url: page.url || window.location.pathname
    }
  });

  return true;
};

export const resetSentryStateForTests = () => {
  initialized = false;
};
